import React, { useMemo, useEffect, useState } from 'react';
import { VeltProvider, getVeltApiKey } from '../velt';
import { VeltPresence, useIdentify } from '@veltdev/react'; // removed VeltComments
import { useLocation } from 'react-router-dom';
import ApiService from '../services/api'; // new import
import ErrorBoundary from './ErrorBoundary';
import { safeLocalStorage } from '../utils/safeStorage';

const IDENTIFY_ENABLED = !['false','0'].includes(String(process.env.REACT_APP_VELT_IDENTIFY_ENABLED || '').toLowerCase());

// NEW: user identification component (safe hook ordering)
function useSafeIdentify() {
  try {
    // Call hook unconditionally to satisfy rules of hooks.
    return useIdentify() || {};
  } catch {
    return {};
  }
}

function IdentifyUser({ roomId, onIdentified, enabled }) {
  const { identify, isIdentified } = useSafeIdentify(); // always invoked to keep hook order stable

  useEffect(() => {
    if (!enabled || !identify || isIdentified) return; // wait for enabled & hook
    let aborted = false;
    (async () => {
      try {
        const me = await ApiService.getMe().catch(() => null);
        if (aborted) return;
        if (me) {
          const payload = {
            userId: me.id || me._id,
            name: me.name || me.email || 'User',
            email: me.email,
            photoUrl: me.avatarUrl || me.avatar || undefined, // Velt fetches remotely; Google URL is fine server-side
            groupId: me?.tenant?.id || me?.tenant?._id || me?.tenant?.slug || 'default'
          };
          await identify(payload);
          onIdentified && onIdentified(payload);
          console.info('[Velt] Identified user', (me.id || me._id));
        } else {
          const anonId = `anon-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
          const payload = { userId: anonId, name: 'Guest', email: `${anonId}@local` };
          await identify(payload);
          onIdentified && onIdentified(payload);
          console.info('[Velt] Identified anonymous user');
        }
      } catch (e) {
        console.warn('[Velt] identify failed', e?.message || e);
      }
    })();
    return () => { aborted = true; };
  }, [enabled, identify, isIdentified, roomId, onIdentified]);
  return null;
}

export const VeltProviders = ({ children }) => {
  const apiKey = getVeltApiKey();
  const location = useLocation();

  const roomId = useMemo(() => {
    // Build a stable room/doc id: tenantSlug + route path
    let tenantPart = 'global';
    try {
      const cached = safeLocalStorage.getItem('testara_currentUser');
      if (cached) {
        const me = JSON.parse(cached);
        tenantPart = (me?.tenant?.slug || me?.tenant?.id || 'global');
      }
    } catch {}
    const path = (location?.pathname || '/').replace(/\/+$/, '') || '/';
    return `testara:${tenantPart}${path}`;
  }, [location?.pathname]);

  const [identifiedUser, setIdentifiedUser] = useState(null);
  // Removed comment mode & floating button per user request

  useEffect(() => {
    if (apiKey) {
      const masked = apiKey.length > 6 ? `${apiKey.slice(0,3)}…${apiKey.slice(-3)}` : '[set]';
      console.info('[Velt] Provider active', { roomId, apiKey: masked });
    } else {
      console.info('[Velt] Provider inactive (no API key)');
    }
  }, [apiKey, roomId]);

  if (!apiKey) {
    return children;
  }
  return (
    <ErrorBoundary
      fallback={(err) => {
        console.warn('[Velt] Collaboration provider error, falling back to standalone shell:', err);
        return children;
      }}
    >
      <VeltProvider apiKey={apiKey}>
        {/* Identify current user once */}
        <IdentifyUser roomId={roomId} onIdentified={setIdentifiedUser} enabled={IDENTIFY_ENABLED} />
        {/* Presence bound to room */}
        <VeltPresence documentId={roomId} docId={roomId} room={roomId} />
        {/* Commentable surface wrapper */}
        <div id="comment-surface" className="relative min-h-full">
          {children}
        </div>
        {/* NOTE: Floating Add Comment button removed per user request */}
        {process.env.NODE_ENV !== 'production' && identifiedUser && IDENTIFY_ENABLED && (
          <div className="fixed bottom-0 left-0 m-2 px-2 py-1 rounded bg-black/60 text-[10px] leading-tight font-mono text-white z-50 shadow">
            <div>room: {roomId}</div>
            <div>user: {identifiedUser.userId}</div>
          </div>
        )}
      </VeltProvider>
    </ErrorBoundary>
  );
};

export default VeltProviders;
